import React, { useEffect, useState } from 'react';
import { Card, Select, Spin, Typography, Tag, Table, Button, Input, Form } from 'antd';
import { editImport, loadVarieties, updateImport } from '@app/api/import.api';
import { Modal } from 'antd';

const { Option } = Select;
const { Title, Text } = Typography;

interface PackingListProps {
  import_id: string | null;
  editable: boolean;
  editableUser: any;
  importStatus: string | null;
  onPackingListChange?: (lines: any[]) => void;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const debounce = (func: Function, delay: number) => {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const PackingListEdit: React.FC<PackingListProps> = ({
  import_id,
  editable,
  editableUser,
  importStatus,
  onPackingListChange,
}) => {
  const [productOptions, setProductOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [varieties, setVarieties] = useState<any[]>([]);
  const [selectedVarieties, setSelectedVarieties] = useState<number[]>([]);
  const [displayedVarieties, setDisplayedVarieties] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  //const [labVerification, setLabVerification] = useState<string>('');
  const [brandName, setBrandName] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [newRows, setNewRows] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = editableUser && editable && importStatus === 'PENDING_DOCUMENT';

  useEffect(() => {
    if (!import_id) return;

    const fetchImportData = async () => {
      try {
        const response = await editImport(Number(import_id));
        console.log('API Response:', response);

        const productOptions = response.product_options || [];
        setProductOptions(productOptions);

        if (!response.import) {
          setError('Import data is missing.');
          setTableData([]);
          return;
        }

        const formattedData = response.import.import_lines.flatMap((importLine: any) => {
          const product = productOptions.find((p) => p.value === importLine.product_id);

          return importLine.import_line_varieties.map((variety: any) => {
            const L4 = variety.L4 || {};

            return {
              key: `${importLine.product_id}-${variety.product_variety_id}`,
              labVerification: variety.lab_verification,
              productSpecies: L4.species || 'Unknown',
              productVarieties: variety.product_variety?.name || 'Unknown',
              brandName: Array.isArray(L4.brand_names) ? L4.brand_names.join(', ') : L4.brand_names || 'Unknown',
              quantity: variety.quantity,
            };
          });
        });

        console.log('Formatted Table Data:', formattedData);
        setTableData(formattedData);
      } catch (error) {
        console.error('Failed to fetch product options:', error);
        setError('Failed to fetch product options.');
      }
    };

    fetchImportData();
  }, [import_id]);

  useEffect(() => {
    if (selectedProduct) {
      fetchVarietiesDebounced(selectedProduct);
    }
  }, [selectedProduct]);

  const fetchVarietiesDebounced = debounce(async (productId: number) => {
    try {
      setLoading(true);

      const response = await loadVarieties(Number(import_id), productId);

      //console.log('RAW RESPONSE:', response);

      const rawVarieties = response;

      if (!Array.isArray(rawVarieties)) {
        setVarieties([]);
        updateDisplayedVarieties([], searchTerm, visibleCount);
        return;
      }

      setVarieties(rawVarieties);
      updateDisplayedVarieties(rawVarieties, searchTerm, visibleCount);

      const mappedVarieties = rawVarieties.map((item: any) => ({
        id: item.value,
        name: item.label,
      }));

      setVarieties(rawVarieties);
      updateDisplayedVarieties(rawVarieties, searchTerm, visibleCount);
    } catch {
      setError('Failed to load varieties.');
      setVarieties([]);
      updateDisplayedVarieties([], searchTerm, visibleCount);
    } finally {
      setTimeout(() => setLoading(false), 100);
    }
  }, 100);

  const formatLinesForSubmit = (rows: any[]) => {
    return rows.reduce((acc, row) => {
      const productId = productOptions.find((opt) => opt.label === row.productSpecies)?.value;
      if (!productId) return acc;

      const varietyData = (row.productVarietyIds ?? [])
        .map((varietyId: number) => ({
          id: null,
          product_variety_id: varietyId,
          L4: {
            species: row.productSpecies,
            brand_names: row.brandName,
          },
          quantity: Number(row.quantity),
        }))
        .filter(Boolean);

      const existing = acc.find((item: any) => item.product_id === productId);
      if (existing) {
        existing.import_line_varieties.push(...varietyData);
        existing.quantity += Number(row.quantity);
      } else {
        acc.push({
          id: null,
          product_id: productId,
          import_line_varieties: varietyData,
          quantity: Number(row.quantity),
        });
      }

      return acc;
    }, []);
  };

  const fetchImportData = async () => {
    if (!import_id) return;
    try {
      const response = await editImport(Number(import_id));

      const productOptions = response.product_options || [];
      setProductOptions(productOptions);

      let formattedData: any[] = [];
      if (response.import && Array.isArray(response.import.import_lines)) {
        formattedData = response.import.import_lines.flatMap(
          (importLine: { product_id: any; import_line_varieties: any[] }) => {
            const product = productOptions.find((p) => p.value === importLine.product_id);

            return importLine.import_line_varieties.map((variety) => ({
              key: `${importLine.product_id}-${variety.product_variety_id}`,
              labVerification: variety.lab_verification,
              productSpecies: variety.L4.species || 'Unknown',
              productVarieties: variety.product_variety.name,
              brandName: variety.L4.brand_names,
              quantity: variety.quantity,
            }));
          },
        );
      } else {
        setError('Import data is missing.');
      }

      setTableData(formattedData); // Set the formatted data in state
    } catch (error) {
      console.error('Failed to fetch product options:', error);
      setError('Failed to fetch product options.');
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleCount((prev) => Math.min(prev + 50, varieties.length));
    }
  };

  const updateDisplayedVarieties = (allVarieties: any[] = [], search: string, limit: number) => {
    if (!Array.isArray(allVarieties)) {
      setDisplayedVarieties([]);
      return;
    }

    const filtered = allVarieties.filter((variety) =>
      (variety?.label || '').toLowerCase().includes(search.toLowerCase()),
    );

    setDisplayedVarieties(filtered.slice(0, limit));
  };

  useEffect(() => {
    if (!Array.isArray(varieties) || varieties.length === 0) {
      setDisplayedVarieties([]);
      return;
    }

    updateDisplayedVarieties(varieties, searchTerm, visibleCount);
  }, [searchTerm, varieties, visibleCount]);

  const toggleVarietySelection = (varietyId: number) => {
    // Check if the variety is disabled based on being already used or the editability of the row
    if (isVarietyDisabled(varietyId)) return;

    setSelectedVarieties((prev) =>
      prev.includes(varietyId) ? prev.filter((id) => id !== varietyId) : [...prev, varietyId],
    );
  };

  const isVarietyDisabled = (varietyId: number) => {
    const isVarietyUsed = tableData.some((row) => {
      if (row.key === editingKey) return false;
      return row.productVarieties.split(', ').includes(varietyId.toString());
    });

    return isVarietyUsed || !canEdit;
  };

  const handleProductChange = (productId: number) => {
    setSelectedProduct(productId);
    setSelectedVarieties([]);
    setError(null);

    if (productId) {
      fetchVarietiesDebounced(productId);
    }
  };

  const handleEdit = (record: any) => {
    setEditingKey(record.key);
    setIsEditing(true); // enter editing mode
    // setLabVerification(record.labVerification);
    setBrandName(record.brandName);
    setQuantity(record.quantity);
    setSelectedVarieties(record.productVarietyIds || []);
    setSelectedProduct(productOptions.find((opt) => opt.label === record.productSpecies)?.value || null);
  };

  const handleAdd = () => {
    if (!selectedProduct || !brandName || quantity <= 0) {
      setError('Please fill in all fields and ensure quantity is greater than 0.');
      return;
    }

    const newRow = {
      key: `new-${Date.now()}`,
      // labVerification,
      productSpecies: productOptions.find((opt) => opt.value === selectedProduct)?.label || '',
      productVarietyIds: [...selectedVarieties],
      productVarieties: selectedVarieties.map((id) => varieties.find((v) => v.value === id)?.label).join(', '),
      brandName,
      quantity,
      isNew: true,
    };

    const updatedTable = [newRow, ...tableData];
    setNewRows([newRow, ...newRows]);
    setTableData(updatedTable);
    onPackingListChange?.(formatLinesForSubmit(updatedTable)); // ← notify parent

    // setLabVerification('');
    setBrandName('');
    setQuantity(0);
    setSelectedVarieties([]);
  };

  const handleUpdate = () => {
    if (!selectedProduct || !brandName || !quantity) {
      setError('Please fill in all fields.');
      return;
    }

    const updatedRow = {
      key: editingKey!,
      // labVerification,
      productSpecies: productOptions.find((opt) => opt.value === selectedProduct)?.label || '',
      productVarietyIds: [...selectedVarieties],
      productVarieties: selectedVarieties.map((id) => varieties.find((v) => v.value === id)?.label).join(', '),
      brandName,
      quantity,
      isNew: true,
    };

    const updatedData = tableData.map((row) => (row.key === editingKey ? updatedRow : row));
    setTableData(updatedData);
    onPackingListChange?.(formatLinesForSubmit(updatedData)); // ← notify parent

    setEditingKey(null);
    setIsEditing(false);
    // setLabVerification('');
    setBrandName('');
    setQuantity(0);
    setSelectedVarieties([]);
  };

  const handleDelete = (key: string) => {
    const updatedTable = tableData.filter((row) => row.key !== key);
    setNewRows(newRows.filter((row) => row.key !== key));
    setTableData(updatedTable);
    onPackingListChange?.(formatLinesForSubmit(updatedTable)); // ← notify parent
  };

  const columns = [
    //  { title: 'Lab Verification', dataIndex: 'labVerification' },
    { title: 'Product Species (L3)', dataIndex: 'productSpecies' },
    { title: 'Product Variety', dataIndex: 'productVarieties' },
    { title: 'Brand Name', dataIndex: 'brandName' },
    { title: 'Quantity (Tons)', dataIndex: 'quantity' },
    {
      title: 'Actions',
      render: (_: any, record: { isNew: any; key: string }) =>
        record.isNew ? (
          <>
            <Button onClick={() => handleEdit(record)} style={{ marginRight: 8 }}>
              Edit
            </Button>
            <Button danger onClick={() => handleDelete(record.key)}>
              Delete
            </Button>
          </>
        ) : null,
    },
  ];

  // console.log('varieties:', varieties);
  // console.log('displayed:', displayedVarieties);

  return (
    <Card>
      <Title level={4}>Packing List</Title>

      <Select
        style={{ width: '100%', marginBottom: 16 }}
        placeholder="Select a product (L2)"
        onChange={handleProductChange}
        value={selectedProduct}
        showSearch
        disabled={!canEdit}
      >
        {productOptions.map((product) => (
          <Option key={product.value} value={product.value}>
            {product.label}
          </Option>
        ))}
      </Select>

      <Input
        placeholder="Search Varieties(L3)..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 8, width: '100%' }}
        disabled={!canEdit}
      />

      <div style={{ maxHeight: 250, overflowY: 'auto', padding: '8px' }} onScroll={handleScroll}>
        {loading ? (
          <Spin size="large" />
        ) : displayedVarieties.length === 0 ? (
          <Text type="secondary">No varieties available.</Text>
        ) : (
          displayedVarieties.map((variety) => {
            const isVarietyUsed = tableData.some((row) => {
              if (row.key === editingKey) return false; // allow editing row's varieties
              return row.productVarieties.split(', ').includes(variety.label);
            });

            const isDisabled = isVarietyUsed || !canEdit;

            const handleTagChange = () => {
              if (!isDisabled) {
                toggleVarietySelection(variety.value);
              }
            };

            return (
              <Tag.CheckableTag
                key={variety.value}
                checked={selectedVarieties.includes(variety.value)}
                style={{
                  margin: 6,
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: 6,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  border: '2px solid rgb(201, 79, 79)',
                }}
                onClick={handleTagChange} // Handle click instead of onChange
              >
                {variety.label} {isDisabled && <Text type="secondary">(Already added)</Text>}
              </Tag.CheckableTag>
            );
          })
        )}
      </div>

      <Form layout="vertical" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          {/* <Form.Item label="Lab Verification" style={{ flex: 1 }}>
            <Input
              value={labVerification}
              onChange={(e) => setLabVerification(e.target.value)}
              style={{ width: '100%', padding: 6, borderRadius: 6 }}
            />
          </Form.Item> */}

          <Form.Item label="Brand Name" style={{ flex: 1 }}>
            <Input
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              style={{ width: '100%', padding: 6, borderRadius: 6 }}
            />
          </Form.Item>

          <Form.Item label="Quantity (Tons)" style={{ flex: 1 }}>
            <Input
              type="number"
              value={quantity}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || !isNaN(Number(value))) {
                  setQuantity(Number(value));
                }
              }}
              style={{ width: '100%', padding: 5, borderRadius: 6 }}
            />
          </Form.Item>
        </div>
      </Form>

      <br />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        {isEditing ? (
          <Button type="primary" onClick={handleUpdate}>
            Update Item
          </Button>
        ) : (
          <Button type="primary" onClick={handleAdd}>
            Add Item
          </Button>
        )}
      </div>

      <Table columns={columns} dataSource={tableData} pagination={false} style={{ marginTop: 20 }} />
    </Card>
  );
};

export default PackingListEdit;
